import React, { useContext, useState, useMemo, useCallback } from 'react';
import { Layout, Alert, Row, Col, Card, Typography, Input, Button, Spin, Empty, Tabs, DatePicker, Space, message } from 'antd';
import { SearchOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Chart } from 'iconsax-react';
import { GlobalContext } from '../../context/globalContext';
import type { Dayjs } from 'dayjs';
import CppdPdfButton from '../../components/CppdPdfButton';

// Importar hook e componentes
import useCPPDDashboard from '../../hooks/useCPPDDashboard';
import CPPDTimelineReuniao from '../../components/dashboards/Governanca/cppd/CPPDTimelineReuniao';
import CPPDTaskCard from '../../components/dashboards/Governanca/cppd/CPPDTaskCard';
import CPPDMembrosTable from '../../components/dashboards/Governanca/cppd/CPPDMembrosTable';
import CPPDReunioesChart from '../../components/dashboards/Governanca/cppd/CPPDReunioesChart';
import CPPDPautasChart from '../../components/dashboards/Governanca/cppd/CPPDPautasChart';
import CPPDTimelineEvents from '../../components/dashboards/Governanca/cppd/CPPDTimelineEvents';
import CPPDEvolucaoChart from '../../components/dashboards/Governanca/cppd/CPPDEvolucaoChart';
import NewUser from '../../components/NewUser/NewUser';

const { Content } = Layout;
const { Title } = Typography;
const { RangePicker } = DatePicker;

// Tipos locais simplificados
interface AdaptedReuniao {
 id: number;
 nome: string;
 data: string;
 status: 'Concluído' | 'Em andamento' | 'Pendente';
}

const CPPDPage: React.FC = () => {
 const [searchText, setSearchText] = useState('');
 const [showNewUserModal, setShowNewUserModal] = useState(false);
 const [activeTab, setActiveTab] = useState('1');
 const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
 
 const globalContext = useContext(GlobalContext);
 const currentCliente = globalContext?.currentCliente;
 
 const { data, loading, error, refetch, updateFilters } = useCPPDDashboard();

 // Adaptar reuniões
 const adaptedReunioes = useMemo((): AdaptedReuniao[] => {
   if (!data?.proximasReunioes) return [];
   
   return data.proximasReunioes.map((reuniao, index) => ({
     id: typeof reuniao.id === 'number' ? reuniao.id : 
         typeof reuniao.id === 'string' ? parseInt(reuniao.id) || index : index,
     nome: reuniao.nome || 'Reunião sem nome',
     data: reuniao.data || '',
     status: reuniao.status === 'Concluído' ? 'Concluído' :
             reuniao.status === 'Em andamento' ? 'Em andamento' :
             'Pendente'
   }));
 }, [data?.proximasReunioes]);

 // Adaptar membros
 const adaptedMembros = useMemo(() => {
   if (!data?.membrosData) return [];
   
   return data.membrosData.map((membro, index) => ({
     id: membro.id || index,
     nome: membro.nome || 'Sem nome',
     name: membro.nome || 'Sem nome',
     email: membro.email || '',
     funcao: membro.funcao || 'Membro',
     status: (membro.status as 'Ativo' | 'Pendente' | 'Inativo') || 'Ativo',
     taxaParticipacao: membro.taxaParticipacao || 0,
     convidado: membro.convidado || 0,
     presente: membro.presente || 0
   }));
 }, [data?.membrosData]);

 // Adaptar reuniões por status
 const reunioesPorStatus = useMemo(() => {
   if (!data?.reunioesPorStatus) return [];
   
   return Object.entries(data.reunioesPorStatus).map(([status, count]) => ({
     status,
     count: typeof count === 'number' ? count : 0
   }));
 }, [data?.reunioesPorStatus]);

 // Handlers
 const handleSearch = useCallback((value: string) => {
   setSearchText(value);
 }, []);

 const handleDateRangeChange = useCallback((dates: any) => {
   setDateRange(dates);
   
   if (dates && dates.length === 2) {
     updateFilters({
       dataInicio: dates[0].format('YYYY-MM-DD'),
       dataFim: dates[1].format('YYYY-MM-DD')
     });
   } else {
     updateFilters({});
   }
 }, [updateFilters]);

 const handleReload = useCallback(() => {
   if (!currentCliente?._id) {
     message.error('Selecione um cliente para carregar dados');
     return;
   }
   
   message.loading('Atualizando dados...', 0.5);
   refetch();
 }, [currentCliente, refetch]);

 // Estado de carregamento
 if (loading && !data) {
   return (
     <Content style={{ padding: '0 24px 24px' }}>
       <div style={{ textAlign: 'center', padding: '50px 0' }}>
         <Spin size="large" tip="Carregando dashboard..." />
       </div>
     </Content>
   );
 }

 // nenhum cliente selecionado
 if (!currentCliente?._id) {
   return (
     <Content style={{ padding: '0 24px 24px' }}>
       <div style={{ textAlign: 'center', padding: '50px 0' }}>
         <Alert
           message="Cliente não selecionado"
           description="Por favor, selecione um cliente para visualizar o dashboard CPPD"
           type="info"
           showIcon
         />
       </div>
     </Content>
   );
 }

 return (
   <Content style={{ padding: '0 24px 24px' }}>
     {/* Header */}
     <div style={{ marginBottom: 24 }}>
       <Title level={2} style={{ margin: 0 }}>
         <Chart size={22} style={{ marginRight: 8 }} /> Dashboard CPPD
       </Title>
     </div>
     
     {/* Filtros */}
     <Card style={{ marginBottom: 20 }}>
       <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
         <div>
           <Title level={5} style={{ marginBottom: 8 }}>Período</Title>
           <Space>
             <RangePicker 
               format="DD/MM/YYYY"
               placeholder={['Data inicial', 'Data final']}
               value={dateRange}
               onChange={handleDateRangeChange}
             />
             <Button 
               icon={<ReloadOutlined />} 
               onClick={handleReload}
               loading={loading}
             >
               Atualizar
             </Button>
           </Space>
         </div>
         <div>
           <CppdPdfButton 
             clienteId={currentCliente._id}
             dashboardData={data}
             texto="Gerar Relatório"
           />
         </div>
       </div>
     </Card>
     
     {/* Reuniões */}
     <Card style={{ marginBottom: 20 }}>
       <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
         <Title level={4} style={{ margin: 0 }}>Reuniões</Title>
         {adaptedReunioes.length > 0 && (
           <span style={{ color: '#6B7280' }}>Total: {adaptedReunioes.length} reuniões</span>
         )}
       </div>
       
       {adaptedReunioes.length > 0 ? (
         <CPPDTimelineReuniao reunioes={adaptedReunioes} />
       ) : (
         <Empty description="Nenhuma reunião encontrada" />
       )}
     </Card>
     
     {/* Tarefas */}
     <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
       {['Pendentes', 'Em Andamento', 'Concluído'].map((title, index) => {
         const tarefa = data?.tarefas?.[index];
         return (
           <Col xs={24} md={8} key={index}>
             <Card style={{ height: '100%' }}>
               <Title level={4} style={{ marginBottom: 20 }}>{title}</Title>
               {tarefa ? (
                 <CPPDTaskCard
                   titulo={tarefa.titulo || title}
                   percentual={tarefa.percentual || 0}
                   respostasObtidas={tarefa.respostasObtidas || 0}
                   planosAcaoIniciados={tarefa.planosAcaoIniciados || 0}
                 />
               ) : (
                 <Empty description="Nenhuma tarefa encontrada" />
               )}
             </Card>
           </Col>
         );
       })}
     </Row>
     
     {/* Tabs Membros */}
     <Tabs activeKey={activeTab} onChange={setActiveTab} type="card">
       <Tabs.TabPane tab="Membros" key="1">
         <Card>
           <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
             <Title level={4} style={{ margin: 0 }}>Membros</Title>
             <div style={{ display: 'flex', alignItems: 'center' }}>
               <Input
                 placeholder="Buscar membro"
                 value={searchText}
                 onChange={(e) => handleSearch(e.target.value)}
                 style={{ width: 300, marginRight: 10 }}
                 suffix={<SearchOutlined />}
               />
               <Button 
                 type="primary" 
                 icon={<PlusOutlined />} 
                 onClick={() => setShowNewUserModal(true)}
               >
                 Novo usuário
               </Button>
             </div>
           </div>
           
           {adaptedMembros.length > 0 ? (
             <CPPDMembrosTable 
               data={adaptedMembros as any}
               loading={loading}
               searchText={searchText}
             />
           ) : (
             <Empty description="Nenhum membro encontrado" />
           )}
         </Card>
       </Tabs.TabPane>
       
       <Tabs.TabPane tab="Reuniões" key="2">
         <Row gutter={[16, 16]}>
           <Col xs={24} md={12}>
             <Card>
               <Title level={4} style={{ marginBottom: 20 }}>Status das Reuniões</Title>
               {reunioesPorStatus.length > 0 ? (
                 <CPPDReunioesChart data={reunioesPorStatus} />
               ) : (
                 <Empty description="Nenhum dado disponível" />
               )}
             </Card>
           </Col>
           <Col xs={24} md={12}>
             <Card>
               <Title level={4} style={{ marginBottom: 20 }}>Pautas por Regimento</Title>
               {data?.pautasPorRegimentoArray?.length ? (
                 <CPPDPautasChart data={data.pautasPorRegimentoArray} />
               ) : (
                 <Empty description="Nenhuma pauta encontrada" />
               )}
             </Card>
           </Col>
         </Row>
       </Tabs.TabPane>

       {/* Tab Timeline */}
       <Tabs.TabPane tab="Timeline" key="3">
         <Card>
           <Title level={4} style={{ marginBottom: 20 }}>Linha do Tempo de Eventos</Title>
           {data?.timeline?.length ? (
             <CPPDTimelineEvents timeline={data.timeline} />
           ) : (
             <Empty description="Nenhum evento encontrado na timeline" />
           )}
         </Card>
       </Tabs.TabPane>
     </Tabs>
     
     {/* Error Alert */}
     {error && (
       <Alert
         message="Erro ao carregar dados"
         description={error}
         type="error"
         showIcon
         action={<Button onClick={handleReload}>Tentar novamente</Button>}
         style={{ marginTop: 20 }}
       />
     )}

     {/* Modal */}
     <NewUser 
       showModal={showNewUserModal} 
       setShowModal={setShowNewUserModal}
       type="grupo"
     />
   </Content>
 );
};

export default CPPDPage;